import React from "react";
import { Grid } from "@material-ui/core";

import { REGEX_NO_CAPITAL_NO_WHITE_SPACE } from "../../../utils/constants";
import { END_DATE, START_DATE } from "../../../types/templates/brief-fields.constants";
import { TemplateName } from "../../../types/templates/template-name";
import { useWorkOrderTemplateOptions } from "../../../pages/work-order/types/work-order-template.hooks";
import { WORK_ORDER_HOOK } from "../../../pages/work-order/types/work-order-template.interface";

import { IS_REGIONAL } from "../../new-execution-briefs/constants";
import { BorderedBlock } from "../../common-attributes/components/bordered-block/bordered-block.component";
import { CheckboxField } from "../../forms/fields/checkbox-field/checkbox-field.component";
import { DateField } from "../../forms/fields/date-field/date-field.component";
import { FieldArray } from "../../forms/fields/field-array/field-array.component";
import { RichTextField } from "../../forms/fields/rich-text-field/components/rich-text-field/rich-text-field.component";
import { SelectField } from "../../forms/fields/select-field/select-field.component";
import { StatusSection } from "../../common-attributes/components/status-section/status-section.component";
import { TextField } from "../../forms/fields/text-field/text-field.component";
import { UserSearchField } from "../../forms/fields/user-search-field/user-search-field.component";

import {
  ARE_VENDOR_ASSETS_PROVIDED,
  ASSET_LANGUAGES,
  BRANDS_INCLUDED,
  CAMPAIGN_TAG,
  CONTENT_NAME,
  COPY_DIRECTION,
  DUE_AT,
  FUNNEL,
  HAS_VENDOR_COMMITMENT,
  IS_BUY_GET,
  IS_CREATIVE_REVIEW_REQUIRED,
  OVERALL_COMMUNICATION_APPROACH,
  PHD_BUDGET,
  PROJECT_OVERVIEW,
  REPORTING_MASTER_CAMPAIGN_NAME,
  SKU_LIST_REQUIRED,
  THEME,
  TRAFFICKING_DUE_DATE,
  UPLOAD_LOCATION,
  UPLOAD_LOCATION_SKU_LIST,
  VENDOR_APPROVAL_REQUIRED,
  VENDOR_ASSETS_LOCATION,
} from "../constants";

import { AdSection } from "./paid-media/components/ad-section/ad-section.component";
import { UnitLinks } from "./paid-media/components/unit-links/unit-links.component";
import { UnitLink } from "./paid-media/types/unit-link/unit-link.interface";
import { UnitLinkLanguage } from "./paid-media/types/unit-link/unit-link-language.type";
import { UserSearchFieldSelection } from "./common/user-search-field-selection/user-search-field-selection.component";
import { IWorkOrderTemplate, pageType } from ".";
import { HiddenHeading } from "../../../pages/pages.styles";

export const WorkOrderPaidMediaTemplate = (props: IWorkOrderTemplate) => {
  const { languagesOptions, regionsOptions } = useWorkOrderTemplateOptions([
    WORK_ORDER_HOOK.LANGUAGES,
    WORK_ORDER_HOOK.REGIONS,
  ]);

  const appendUnitLink = (language: UnitLinkLanguage): UnitLink => ({
    id: null,
    language,
    unitLink: "",
    workOrder: props.resourceId,
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <HiddenHeading>{props.pageTitle}</HiddenHeading>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={PROJECT_OVERVIEW.TITLE}
            name={PROJECT_OVERVIEW.ID}
            required
            subTitle={TemplateName.WorkOrder.PaidMedia}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={COPY_DIRECTION.TITLE}
            name={COPY_DIRECTION.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DateField
                form={props.form}
                label={START_DATE.TITLE}
                name={START_DATE.ID}
                required
                userPermissions={props.userPermissions}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateField
                form={props.form}
                label={END_DATE.TITLE}
                name={END_DATE.ID}
                required
                userPermissions={props.userPermissions}
              />
            </Grid>
          </Grid>
        </BorderedBlock>
        <BorderedBlock>
          <SelectField
            form={props.form}
            label={OVERALL_COMMUNICATION_APPROACH.TITLE}
            name={OVERALL_COMMUNICATION_APPROACH.ID}
            options={[
              { value: "CONSIDERATION", label: "Consideration" },
              { value: "AWARENESS", label: "Awareness" },
              { value: "TACTICAL", label: "Tactical" },
            ]}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <TextField
            form={props.form}
            inputPattern={REGEX_NO_CAPITAL_NO_WHITE_SPACE}
            label={REPORTING_MASTER_CAMPAIGN_NAME.TITLE}
            name={REPORTING_MASTER_CAMPAIGN_NAME.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <TextField
            form={props.form}
            inputPattern={REGEX_NO_CAPITAL_NO_WHITE_SPACE}
            label={CAMPAIGN_TAG.TITLE}
            name={CAMPAIGN_TAG.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <TextField
            form={props.form}
            inputPattern={REGEX_NO_CAPITAL_NO_WHITE_SPACE}
            label={BRANDS_INCLUDED.TITLE}
            name={BRANDS_INCLUDED.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={CONTENT_NAME.TITLE}
            name={CONTENT_NAME.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={THEME.TITLE}
            name={THEME.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={UPLOAD_LOCATION.TITLE}
            name={UPLOAD_LOCATION.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={PHD_BUDGET.TITLE}
            name={PHD_BUDGET.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <FieldArray
            appendField={() => appendUnitLink("English")}
            buttonText="Add Link"
            FieldArrayField={UnitLinks}
            form={props.form}
            label="Unit Links - English"
            name="englishAssetUnitLinks"
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <FieldArray
            appendField={() => appendUnitLink("French")}
            buttonText="Add Link"
            FieldArrayField={UnitLinks}
            form={props.form}
            label="Unit Links - French"
            name="frenchAssetUnitLinks"
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
      </Grid>
      <Grid item xs={12} md={4}>
        <StatusSection
          form={props.form}
          label="Status"
          name="status"
          pageType={pageType}
          userPermissions={props.userPermissions}
        />
        <BorderedBlock>
          <UserSearchField
            form={props.form}
            label="Assign"
            name="contentCreatorsSelection"
            resourceId={props.resourceId}
            resourceType={props.resourceType}
            SelectionComponent={UserSearchFieldSelection}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <SelectField
            form={props.form}
            label={ASSET_LANGUAGES.TITLE}
            multiple
            name={ASSET_LANGUAGES.ID}
            options={languagesOptions}
            placeholder="Select Language"
            required
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <CheckboxField
            form={props.form}
            label={IS_CREATIVE_REVIEW_REQUIRED.TITLE}
            name={IS_CREATIVE_REVIEW_REQUIRED.ID}
            userPermissions={props.userPermissions}
          />
          <CheckboxField
            form={props.form}
            label={IS_BUY_GET.TITLE}
            name={IS_BUY_GET.ID}
            userPermissions={props.userPermissions}
          />
          <CheckboxField
            form={props.form}
            label={HAS_VENDOR_COMMITMENT.TITLE}
            name={HAS_VENDOR_COMMITMENT.ID}
            userPermissions={props.userPermissions}
          />
          <CheckboxField
            form={props.form}
            label={VENDOR_APPROVAL_REQUIRED.TITLE}
            name={VENDOR_APPROVAL_REQUIRED.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <DateField
            form={props.form}
            label={DUE_AT.TITLE}
            name={DUE_AT.ID}
            required
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <DateField
            form={props.form}
            label={TRAFFICKING_DUE_DATE.TITLE}
            name={TRAFFICKING_DUE_DATE.ID}
            required
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <CheckboxField
            form={props.form}
            label={ARE_VENDOR_ASSETS_PROVIDED.TITLE}
            name={ARE_VENDOR_ASSETS_PROVIDED.ID}
            userPermissions={props.userPermissions}
          >
            <RichTextField
              form={props.form}
              label={VENDOR_ASSETS_LOCATION.TITLE}
              name={VENDOR_ASSETS_LOCATION.ID}
              userPermissions={props.userPermissions}
            />
          </CheckboxField>
        </BorderedBlock>
        <BorderedBlock>
          <CheckboxField
            form={props.form}
            label={IS_REGIONAL.TITLE}
            name={IS_REGIONAL.ID}
            userPermissions={props.userPermissions}
          >
            <SelectField
              form={props.form}
              label="Regions"
              multiple
              name="regions"
              options={regionsOptions}
              placeholder="Select Region"
              userPermissions={props.userPermissions}
            />
          </CheckboxField>
        </BorderedBlock>
        <AdSection form={props.form} userPermissions={props.userPermissions} />
        <BorderedBlock>
          <CheckboxField
            form={props.form}
            label={SKU_LIST_REQUIRED.TITLE}
            name={SKU_LIST_REQUIRED.ID}
            userPermissions={props.userPermissions}
          >
            <RichTextField
              form={props.form}
              label={UPLOAD_LOCATION_SKU_LIST.TITLE}
              name={UPLOAD_LOCATION_SKU_LIST.ID}
              userPermissions={props.userPermissions}
            />
          </CheckboxField>
        </BorderedBlock>
        <BorderedBlock>
          <SelectField
            form={props.form}
            label={FUNNEL.TITLE}
            name={FUNNEL.ID}
            options={[
              { value: "UPPER", label: "Upper" },
              { value: "MIDDLE", label: "Middle" },
              { value: "LOW", label: "Low" },
            ]}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
      </Grid>
    </Grid>
  );
};
